import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AppComponent } from 'src/app/app.component';
import { IoService } from '../../../services/io/io.service';

// Record Card Payment: an admin records a credit card payment that was taken
// outside the register (e.g. the processor's virtual terminal) against an open
// purchase. Nothing is charged. Two modes:
//   lookup — enter the processor (Clearent) transaction ID; the server fetches
//            the sale and fills amount / card / auth code from it.
//   manual — type the amount and card details by hand.
// Opened from detail-purchase; closes with { recorded: true, purchaseStatus }.
@Component({
  selector: 'app-record-payment-dialog',
  templateUrl: './record-payment-dialog.component.html',
  styleUrls: ['./record-payment-dialog.component.scss']
})
export class RecordPaymentDialogComponent implements OnInit {
  context: any = null;
  loading = true;
  loadError: string = null;

  mode: 'lookup' | 'manual' = 'lookup';
  ledgerId: string = null;

  // lookup
  processorTransactionId = '';
  lookingUp = false;
  lookup: any = null;
  lookupError: string = null;

  // manual
  amountInput = '';
  cardBrand = '';
  lastFour = '';
  authCode = '';
  manualReference = '';
  note = '';

  confirming = false;
  saving = false;
  errorMessage: string = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RecordPaymentDialogComponent>,
    private ioService: IoService,
  ) { }

  async ngOnInit() {
    try {
      const context: any = await this.ioService.post('/purchase/getRecordPaymentContext', {
        purchaseId: this.data.purchaseId,
      });
      this.context = context;
      if (context.ledgers && context.ledgers.length) {
        const preferred = context.ledgers.find(l => l.key === 'credit') || context.ledgers[0];
        this.ledgerId = preferred._id;
      }
      if (!this.selectedLedger || !this.selectedLedger.lookup) {
        this.mode = 'manual';
      }
      this.amountInput = (context.due / 100).toFixed(2);
      if (context.pendingTransactions > 0) {
        this.loadError = 'This purchase has a pending payment. Resolve it before recording a payment.';
      } else if (context.due <= 0) {
        this.loadError = 'This purchase has no balance due.';
      }
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load purchase.';
    }
    this.loading = false;
  }

  get selectedLedger(): any {
    if (!this.context || !this.context.ledgers) { return null; }
    return this.context.ledgers.find(l => l._id === this.ledgerId) || null;
  }

  get canLookup(): boolean {
    return !!(this.selectedLedger && this.selectedLedger.lookup);
  }

  setMode(mode: 'lookup' | 'manual') {
    this.mode = mode;
    this.errorMessage = null;
    this.confirming = false;
  }

  ledgerChanged() {
    this.lookup = null;
    this.lookupError = null;
    if (!this.canLookup) { this.mode = 'manual'; }
  }

  money(cents: number): string {
    if (cents == null) { return '—'; }
    return (cents < 0 ? '-' : '') + '$' + (Math.abs(cents) / 100).toFixed(2);
  }

  // ---- lookup -------------------------------------------------------------

  async doLookup() {
    const id = this.processorTransactionId.trim();
    if (!id || this.lookingUp) { return; }
    this.lookingUp = true;
    this.lookup = null;
    this.lookupError = null;
    this.errorMessage = null;
    try {
      this.lookup = await this.ioService.post('/purchase/lookupPayment', {
        purchaseId: this.data.purchaseId,
        ledgerId: this.ledgerId,
        processorTransactionId: id,
      });
      if (this.lookup.alreadyRecorded) {
        this.lookupError = 'This transaction is already recorded'
          + (this.lookup.alreadyRecorded.purchaseKey ? ' on purchase ' + this.lookup.alreadyRecorded.purchaseKey : '') + '.';
      } else if (this.lookup.found.type !== 'SALE') {
        this.lookupError = 'That transaction is a ' + this.lookup.found.type + ', not a sale.';
      } else if (this.lookup.found.result !== 'APPROVED') {
        this.lookupError = 'That transaction is ' + (this.lookup.found.result || 'not approved') + '.';
      } else if (this.lookup.amountCents > this.context.due) {
        this.lookupError = 'The processor amount ' + this.money(this.lookup.amountCents)
          + ' exceeds the balance due ' + this.money(this.context.due) + '.';
      }
    } catch (e) {
      this.lookupError = e?.error?.msg || 'Lookup failed.';
    }
    this.lookingUp = false;
  }

  get lookupUsable(): boolean {
    return !!(this.lookup && !this.lookupError && this.lookup.usable);
  }

  // ---- manual -------------------------------------------------------------

  get amountCents(): number | null {
    const value = parseFloat(this.amountInput);
    if (isNaN(value)) { return null; }
    return Math.round(value * 100);
  }

  get manualValid(): boolean {
    const cents = this.amountCents;
    if (cents == null || cents <= 0 || !this.context) { return false; }
    if (cents > this.context.due) { return false; }
    const digits = this.lastFour.replace(/\D/g, '');
    if (digits.length && digits.length !== 4) { return false; }
    return true;
  }

  get manualAmountError(): string {
    const cents = this.amountCents;
    if (this.amountInput === '' || cents == null) { return ''; }
    if (cents <= 0) { return 'Enter the amount that was charged.'; }
    if (this.context && cents > this.context.due) { return 'Exceeds the balance due ' + this.money(this.context.due) + '.'; }
    return '';
  }

  // ---- confirm / submit ---------------------------------------------------

  get summaryAmountCents(): number {
    return this.mode === 'lookup' ? (this.lookup ? this.lookup.amountCents : 0) : (this.amountCents || 0);
  }

  get summaryCard(): string {
    if (this.mode === 'lookup') {
      const f = this.lookup && this.lookup.found;
      if (!f) { return ''; }
      return [f.cardBrand, f.lastFour ? 'xxxx' + f.lastFour : ''].filter(x => x).join(' ');
    }
    return [this.cardBrand, this.lastFour ? 'xxxx' + this.lastFour.replace(/\D/g, '') : ''].filter(x => x).join(' ');
  }

  get summaryAuth(): string {
    if (this.mode === 'lookup') { return this.lookup && this.lookup.found ? this.lookup.found.authCode : ''; }
    return this.authCode.trim();
  }

  get summaryReference(): string {
    return this.mode === 'lookup' ? this.processorTransactionId.trim() : this.manualReference.trim();
  }

  get remainingAfter(): number {
    return this.context ? this.context.due - this.summaryAmountCents : 0;
  }

  canContinue(): boolean {
    if (!this.context || this.loadError) { return false; }
    return this.mode === 'lookup' ? this.lookupUsable : this.manualValid;
  }

  continueToConfirm() {
    if (this.canContinue()) {
      this.errorMessage = null;
      this.confirming = true;
    }
  }

  back() {
    this.confirming = false;
  }

  async submit() {
    if (this.saving || !this.canContinue()) { return; }
    this.saving = true;
    this.errorMessage = null;
    const body: any = {
      purchaseId: this.data.purchaseId,
      ledgerId: this.ledgerId,
      mode: this.mode,
      note: this.note.trim() || null,
    };
    if (this.mode === 'lookup') {
      body.processorTransactionId = this.processorTransactionId.trim();
    } else {
      body.amount = (this.amountCents / 100).toFixed(2);
      body.cardBrand = this.cardBrand || null;
      body.lastFour = this.lastFour.replace(/\D/g, '') || null;
      body.authCode = this.authCode.trim() || null;
      body.processorTransactionId = this.manualReference.trim() || null;
    }
    try {
      const response: any = await this.ioService.post('/purchase/recordPayment', body);
      AppComponent.showSnack(
        'Payment of ' + this.money(this.summaryAmountCents) + ' recorded. Purchase is ' + response.purchaseStatus + '.', 'OK', 4000);
      this.dialogRef.close({ recorded: true, purchaseStatus: response.purchaseStatus });
    } catch (e) {
      this.errorMessage = e?.error?.msg || 'Recording the payment failed.';
      this.confirming = false;
    }
    this.saving = false;
  }

  close() {
    this.dialogRef.close();
  }
}
